import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWA = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);

  // Detectar plataforma y modo
  useEffect(() => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(isIOSDevice);

    const standaloneMode = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;
    setIsStandalone(standaloneMode);

    if (standaloneMode) {
      setIsInstalled(true);
    }
  }, []);

  // Capturar evento beforeinstallprompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      const dismissedDate = dismissed ? new Date(dismissed) : null;
      const daysSinceDismissed = dismissedDate 
        ? (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24) 
        : 999;
      
      if (daysSinceDismissed > 7) {
        setTimeout(() => setShowInstallBanner(true), 30000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Nueva versión disponible (avisada por el service worker)
  useEffect(() => {
    const handler = () => setNeedRefresh(true);
    window.addEventListener('pwa:need-refresh', handler);
    return () => window.removeEventListener('pwa:need-refresh', handler);
  }, []);

  // Monitorear estado de conexión
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    setIsOnline(navigator.onLine);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setShowInstallBanner(false);
    }
    
    setInstallPrompt(null);
    return outcome === 'accepted';
  }, [installPrompt]);

  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  }, []);

  const updateServiceWorker = useCallback((_reloadPage?: boolean) => {
    window.location.reload();
  }, []);

  return {
    canInstall: !!installPrompt,
    isInstalled,
    isIOS,
    isStandalone,
    isOnline,
    showInstallBanner,
    needRefresh,
    promptInstall,
    dismissInstallBanner,
    updateServiceWorker,
    setNeedRefresh,
  };
};
